import { useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";

// Naming note: backend tables are kept frozen for compatibility, but in the UI:
//   - `repositories` row  →  "Project"        (a GitHub project / repo)
//   - `projects`     row  →  "PRD Execution"  (one execution of a PRD inside a project)
// Identifiers below use the old names; user-facing strings use the new ones.

export type Selection =
  | { type: "project"; repositoryId: Id<"repositories">; repoSlug: string; projectId: Id<"projects"> }
  | { type: "repo-home"; repositoryId: Id<"repositories">; repoSlug: string; repoTab: "prds" | "inbox" | "settings" };

export function ProjectSelector({
  selectedRepoId,
  selection,
  onSelectRepo,
  onSelectItem,
}: {
  selectedRepoId: Id<"repositories"> | null;
  selection: Selection | null;
  onSelectRepo: (repoId: Id<"repositories">) => void;
  onSelectItem: (selection: Selection) => void;
}) {
  const projects = useQuery(api.projects.list, {});
  const repositories = useQuery(api.repositories.list, {});

  if (!projects || !repositories) return <div className="text-zinc-600 text-sm">Loading...</div>;

  const repoProjects = selectedRepoId
    ? projects.filter((p) => p.repositoryId === selectedRepoId && !p.archived)
    : [];
  const selectedRepo = repositories.find((r) => r._id === selectedRepoId);
  const ungroupedProjects = projects.filter(
    (p) => !p.repositoryId || !repositories.some((r) => r._id === p.repositoryId)
  );

  return (
    <div className="flex items-center gap-2 min-w-0">
      {/* Repository dropdown */}
      <select
        value={selectedRepoId ?? ""}
        onChange={(e) => {
          if (e.target.value) onSelectRepo(e.target.value as Id<"repositories">);
        }}
        className="bg-zinc-900 border border-zinc-700 rounded-md px-2 sm:px-3 py-1.5 text-sm text-zinc-200 focus:outline-none focus:ring-1 focus:ring-indigo-500 min-w-0 max-w-[140px] sm:max-w-none"
      >
        <option value="" disabled>
          Project...
        </option>
        {repositories.map((r) => (
          <option key={r._id} value={r._id}>
            {r.name}
          </option>
        ))}
      </select>

      {/* PRD dropdown — only shown when a repo is selected */}
      {selectedRepoId && selectedRepo && (
        <>
          <span className="text-zinc-600">/</span>
          <select
            value={
              selection?.type === "project" ? `project:${selection.projectId}` : ""
            }
            onChange={(e) => {
              const val = e.target.value;
              if (val.startsWith("project:")) {
                onSelectItem({
                  type: "project",
                  repositoryId: selectedRepoId,
                  repoSlug: selectedRepo.slug,
                  projectId: val.slice(8) as Id<"projects">,
                });
              }
            }}
            className="bg-zinc-900 border border-zinc-700 rounded-md px-2 sm:px-3 py-1.5 text-sm text-zinc-200 focus:outline-none focus:ring-1 focus:ring-indigo-500 min-w-0 max-w-[140px] sm:max-w-none"
          >
            <option value="" disabled>
              PRD execution...
            </option>
            {repoProjects.map((p) => (
              <option key={p._id} value={`project:${p._id}`}>
                {p.name}
              </option>
            ))}
          </select>

          {/* Repo home button */}
          <button
            onClick={() => {
              window.location.hash = `/${selectedRepo.slug}/prds`;
            }}
            className={`p-1.5 rounded-md border transition-colors ${
              selection?.type === "repo-home"
                ? "bg-indigo-500/15 border-indigo-500/30 text-indigo-300"
                : "border-zinc-700 text-zinc-500 hover:text-zinc-200 hover:border-zinc-500"
            }`}
            title="Project home"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 12 8.954-8.955a1.126 1.126 0 0 1 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
            </svg>
          </button>
        </>
      )}

      {/* Unlinked projects — shown when no repo is selected */}
      {!selectedRepoId && ungroupedProjects.length > 0 && (
        <select
          value={
            selection?.type === "project" &&
            ungroupedProjects.some((p) => p._id === selection.projectId)
              ? `project:${selection.projectId}`
              : ""
          }
          onChange={(e) => {
            const val = e.target.value;
            if (val.startsWith("project:")) {
              const pid = val.slice(8) as Id<"projects">;
              onSelectItem({
                type: "project",
                repositoryId: "" as Id<"repositories">,
                repoSlug: "",
                projectId: pid,
              });
            }
          }}
          className="bg-zinc-900 border border-zinc-700 rounded-md px-3 py-1.5 text-sm text-zinc-200 focus:outline-none focus:ring-1 focus:ring-indigo-500"
        >
          <option value="" disabled>
            Unlinked PRD executions...
          </option>
          {ungroupedProjects.map((p) => (
            <option key={p._id} value={`project:${p._id}`}>
              {p.name}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}
